import React, { FC, useEffect, useState, isValidElement, ReactNode } from 'react';
import classNames from 'classnames/bind';

import Radio, { Id as Value, LabelType, Size } from '../../atoms/Radio';
import Text, { TextProps } from '../../atoms/Text';
import noop from '../../utils/noop';

import styles from './index.pcss';

const cx = classNames.bind(styles);

const renderLegend: (
  legend: LabelType,
  legendTextProps: Partial<TextProps> | undefined,
  legendCls: string
) => ReactNode | null = (legend, legendTextProps, legendCls) => {
  if (legend === undefined || legend === '') {
    return null;
  }

  let innerNode;
  // checking if `legend` is a ReactNode
  if (isValidElement(legend)) {
    innerNode = legend;
  } else {
    // if `legend` is not a ReactNode, use Text component
    const textProps: TextProps = {
      variant: 'T1',
      color: 'Nero',
      weight: 'bold',
      children: [legend],
      ...legendTextProps
    };

    innerNode = Text(textProps);
  }

  return <legend className={legendCls}>{innerNode}</legend>;
};

type Option = {
  label?: LabelType;
  value: Value;
  disabled?: boolean;
};

type Layout = 'vertical' | 'horizontal';

interface RadioGroupProps {
  /** options for radios */
  options: Array<Option>;
  /** name for the same group of radios */
  htmlName: string;
  /** label for the radio group */
  legend?: string;
  checkedValue?: Value;
  disabled?: boolean;
  onChange?: (value: Value) => void;
  /** Size of radio element:
   * `sm`: 24px
   * `md`: 28px
   */
  size?: Size;
  /** Padding between radio element and label:
   * `sm`: 8px
   * `md`: 16px
   */
  radioGutter?: Size;
  /** Padding between radio elements in the radio group */
  groupGutter?: Size;
  layout?: Layout;
  labelTextProps?: Partial<TextProps>;
  legendTextProps?: Partial<TextProps>;
}

const RadioGroup: FC<RadioGroupProps> = (props: RadioGroupProps) => {
  const {
    options,
    checkedValue,
    onChange,
    disabled,
    htmlName,
    labelTextProps,
    radioGutter,
    groupGutter = 'sm',
    size,
    legend,
    layout,
    legendTextProps
  } = props;

  const [activeId, setActiveId] = useState<Value | null>(() => checkedValue || null);

  useEffect(() => {
    if (checkedValue) {
      setActiveId(checkedValue);
    }
  }, [checkedValue]);

  const handleChange: (value: Value) => void = value => {
    setActiveId(value);
    if (onChange) onChange(value);
  };

  const renderOptions: (options: Array<Option>) => Array<ReactNode> = radioOptions => {
    const wrapperClassName = cx({ [`radio-gutter-${groupGutter}`]: !!groupGutter });
    return radioOptions.map((option: Option) => {
      const { value, label, disabled: internalDisabled } = option;
      return (
        <Radio
          key={label}
          id={value}
          onChange={handleChange}
          checked={activeId === option.value}
          htmlName={htmlName}
          disabled={internalDisabled || disabled}
          gutter={radioGutter}
          size={size}
          label={label}
          labelTextProps={labelTextProps}
          wrapperClassName={wrapperClassName}
        />
      );
    });
  };

  const containerCls = cx('container', {
    'container-vertical': layout === 'vertical'
  });
  const legendCls = cx('legend');

  return (
    <div >
      <fieldset className={containerCls}>
      {renderLegend(legend, legendTextProps, legendCls)}
      {renderOptions(options)}
      </fieldset>
    </div>
  );
};

RadioGroup.defaultProps = {
  size: 'sm',
  groupGutter: 'sm',
  radioGutter: 'sm',
  legend: '',
  labelTextProps: {},
  legendTextProps: {},
  onChange: noop,
  checkedValue: undefined,
  disabled: false,
  layout: 'horizontal'
};

export default RadioGroup;
